import React from 'react';
import { Check, ExternalLink, Sparkles } from 'lucide-react';
import { UserProfile } from '../services/api';
import { PaymentButton } from './PaymentComponents';
import { PRO_ACCESS_DAYS } from './SubscriptionFeatures';
import Login from './Login';
import { useSearchParams } from 'react-router-dom';
import '../styles/pro-landing.css';

interface Props {
  userProfile: UserProfile | null;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
}

const ProLanding: React.FC<Props> = ({ userProfile, setUserProfile }) => {
  const [searchParams] = useSearchParams();
  if (searchParams.get('welcome') === '1') {
    return <div className="pro-offer">
      <header className="pro-offer-intro">
        <p className="pro-offer-eyebrow"><Sparkles size={14} /> Eatsy Pro</p>
        <h1>Let’s show you <em>around.</em></h1>
        <p>{userProfile
          ? 'There isn’t an active Pro pass on this account. If you’ve just purchased, check that you’re signed in with the same email address you used at checkout. Your pass may also have expired.'
          : 'Your introduction to great tables starts here. Sign in with the same email address you used to purchase your Pro pass.'}</p>
      </header>
      <section className="pro-offer-pass" style={{ maxWidth: 460, margin: '0 auto' }}>
        <Login userProfile={userProfile} setUserProfile={setUserProfile} signInLabel="Sign in to start your tour" />
        {userProfile && <p className="mt-4 text-sm text-ink-600">You can sign out above to switch accounts. If your pass has expired, return to Availability to purchase a new one.</p>}
        <a href="/availability" className="mt-6 block text-sm underline text-brand-700">Return to Availability</a>
      </section>
    </div>;
  }
  return (
  <div className="pro-offer">
    <header className="pro-offer-intro">
      <p className="pro-offer-eyebrow"><Sparkles size={14} /> Eatsy Pro</p>
      <h1>Your next table. <em>Found.</em></h1>
      <p>We search reservation platforms and individual restaurant sites, bringing together the availability we find for the London restaurants we track.</p>
    </header>

    <div className="pro-offer-layout">
      <section className="pro-offer-video" aria-labelledby="pro-video-title">
        <h2 id="pro-video-title">See it in action.</h2>
        <div className="pro-offer-video-frame">
          <iframe
            src="https://www.youtube.com/embed/kB9tgUv-tns?si=EMeM7uEjhgE64ydU"
            title="Eatsy PRO Feature Demo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
        <a href="https://www.youtube.com/watch?v=kB9tgUv-tns" target="_blank" rel="noopener noreferrer">Watch on YouTube <ExternalLink size={12} /></a>
      </section>

      <section id="pro-checkout" className="pro-offer-pass" aria-labelledby="pro-pass-title">
        <h2 id="pro-pass-title">Your Pro pass</h2>
        <div className="pro-offer-promotion">
          <p className="pro-offer-promotion-label"><Sparkles size={13} aria-hidden="true" /> Double the access</p>
          <p className="pro-offer-duration" aria-label={`Previously 7 days, now ${PRO_ACCESS_DAYS} days of Pro access`}>
            <s aria-hidden="true">7 days</s>
            <span aria-hidden="true">{PRO_ACCESS_DAYS} <span>days</span></span>
          </p>
          <p className="pro-offer-promotion-note">An extra week to find your next great table.</p>
        </div>
        <p className="pro-offer-price">£8.99</p>
        <p className="pro-offer-terms">{PRO_ACCESS_DAYS} days of access. One payment. No auto-renewal.</p>
        <ul>
          <li><Check size={17} /> All the availability we find, in one place</li>
          <li><Check size={17} /> Filter the coming week by restaurant, date & party size</li>
          <li><Check size={17} /> Email alerts for up to 5 favourite restaurants</li>
          <li><Check size={17} /> Go straight to the restaurant’s booking page</li>
        </ul>
        <div className="pro-offer-action">
          {userProfile ? <PaymentButton compact /> : <>
            <Login userProfile={null} setUserProfile={setUserProfile} signInLabel="Sign in to get Pro" />
            <p>Sign in, then purchase your pass.</p>
          </>}
        </div>
      </section>
    </div>
    <p className="pro-offer-footnote">Availability and alerts update after scheduled checks. Tables can change before you book.</p>
  </div>
);
};

export default ProLanding;
